import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Pencil, Trash2, FileText, Download, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { ExcelImportDialog } from '@/components/ExcelImportDialog';
import DocumentPreview from '@/components/DocumentPreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// La destinazione d'uso non e' un dettaglio catastale: guida il riparto
// (tabelle differenziate per abitazioni, uffici, negozi e box).
const DESTINAZIONI = [
  { value: 'ABITAZIONE', label: 'Abitazione' },
  { value: 'UFFICIO', label: 'Ufficio' },
  { value: 'NEGOZIO', label: 'Negozio' },
  { value: 'BOX', label: 'Box / Autorimessa' },
  { value: 'MAGAZZINO', label: 'Magazzino' },
  { value: 'ALLOGGIO_PORTIERE', label: 'Alloggio portiere' },
  { value: 'ALTRO', label: 'Altro' },
];
const etichettaDestinazione = (v) =>
  DESTINAZIONI.find((d) => d.value === v)?.label || null;
const euro = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

export const UnitsTab = ({ condominiumId }) => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [estrattoDialogOpen, setEstrattoDialogOpen] = useState(false);
  const [selectedUnitForEstratto, setSelectedUnitForEstratto] = useState(null);
  const [estrattoYear, setEstrattoYear] = useState(new Date().getFullYear().toString());
  const [generatingPdf, setGeneratingPdf] = useState(false);
  // anteprima dell'estratto conto: si guarda prima di salvarlo
  const [anteprimaEstratto, setAnteprimaEstratto] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);
  const [formData, setFormData] = useState({
    unit_number: '',
    floor: '',
    building: '',
    destination: '',
    monthly_fixed_rate: '',
    square_meters: '',
    cadastral_sheet: '',
    cadastral_parcel: '',
    cadastral_subordinate: '',
    cadastral_category: '',
    cadastral_income: '',
    notes: '',
  });

  useEffect(() => {
    fetchUnits();
  }, [condominiumId]);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/condominiums/${condominiumId}/units`);
      setUnits(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle unità');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = Object.entries(formData).reduce((acc, [key, value]) => {
        if (value !== '') {
          if (['floor'].includes(key)) {
            acc[key] = parseInt(value);
          } else if (['square_meters', 'cadastral_income', 'monthly_fixed_rate'].includes(key)) {
            acc[key] = parseFloat(value);
          } else {
            acc[key] = value;
          }
        }
        return acc;
      }, {});

      if (editingUnit) {
        await axios.put(`${API}/condominiums/${condominiumId}/units/${editingUnit.id}`, data);
        toast.success('Unità aggiornata');
      } else {
        await axios.post(`${API}/condominiums/${condominiumId}/units`, data);
        toast.success('Unità creata');
      }

      setDialogOpen(false);
      resetForm();
      await fetchUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      unit_number: unit.unit_number || '',
      floor: unit.floor || '',
      building: unit.building || '',
      destination: unit.destination || '',
      monthly_fixed_rate: unit.monthly_fixed_rate ?? '',
      square_meters: unit.square_meters || '',
      cadastral_sheet: unit.cadastral_sheet || '',
      cadastral_parcel: unit.cadastral_parcel || '',
      cadastral_subordinate: unit.cadastral_subordinate || '',
      cadastral_category: unit.cadastral_category || '',
      cadastral_income: unit.cadastral_income || '',
      notes: unit.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (unitId) => {
    if (!confirm('Sei sicuro di voler eliminare questa unità?')) return;

    try {
      await axios.delete(`${API}/condominiums/${condominiumId}/units/${unitId}`);
      toast.success('Unità eliminata');
      await fetchUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const resetForm = () => {
    setEditingUnit(null);
    setFormData({
      unit_number: '',
      floor: '',
      building: '',
      destination: '',
      monthly_fixed_rate: '',
      square_meters: '',
      cadastral_sheet: '',
      cadastral_parcel: '',
      cadastral_subordinate: '',
      cadastral_category: '',
      cadastral_income: '',
      notes: '',
    });
  };

  const openEstrattoDialog = (unit) => {
    setSelectedUnitForEstratto(unit);
    setEstrattoYear(new Date().getFullYear().toString());
    setEstrattoDialogOpen(true);
  };

  const handleGenerateEstratto = async () => {
    if (!selectedUnitForEstratto) return;
    
    setGeneratingPdf(true);
    try {
      const formData = new FormData();
      formData.append('year', estrattoYear);
      
      const response = await axios.post(
        `${API}/condominiums/${condominiumId}/units/${selectedUnitForEstratto.id}/estratto-conto`,
        formData,
        { responseType: 'blob' }
      );
      
      // Apre l'anteprima invece di salvare subito: il PDF resta comunque
      // archiviato nei Documenti dell'unita', come prima.
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setAnteprimaEstratto({
        url: window.URL.createObjectURL(blob),
        nomeFile: `Estratto_Conto_${selectedUnitForEstratto.unit_number}_${estrattoYear}.pdf`,
        titolo: `Estratto conto ${selectedUnitForEstratto.unit_number} — ${estrattoYear}`,
      });
      toast.success('Estratto conto pronto');
      setEstrattoDialogOpen(false);
    } catch (error) {
      console.error('Error generating estratto:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la generazione');
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loading && units.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-stone-600">Caricamento...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Unità Immobiliari</CardTitle>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setImportDialogOpen(true)}
            data-testid="import-units-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            Importa
          </Button>
          <Button
            size="sm"
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            data-testid="add-unit-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuova Unità
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {units.length === 0 ? (
          <div className="text-center text-stone-600 py-8">
            Nessuna unità registrata
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Unità</TableHead>
                <TableHead>Destinazione</TableHead>
                <TableHead>Piano</TableHead>
                <TableHead>Scala</TableHead>
                <TableHead>Mq</TableHead>
                <TableHead className="text-right">Rata mensile</TableHead>
                <TableHead>Dati Catastali</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.map((unit) => (
                <TableRow key={unit.id} data-testid={`unit-row-${unit.id}`}>
                  <TableCell className="font-medium">{unit.unit_number}</TableCell>
                  <TableCell>
                    {etichettaDestinazione(unit.destination) ? (
                      <span className="text-xs px-2 py-1 rounded bg-stone-100 text-stone-700">
                        {etichettaDestinazione(unit.destination)}
                      </span>
                    ) : (
                      <span className="text-stone-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>{unit.floor ?? '-'}</TableCell>
                  <TableCell>{unit.building || '-'}</TableCell>
                  <TableCell>{unit.square_meters ? `${unit.square_meters} m²` : '-'}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {unit.monthly_fixed_rate ? `€ ${euro(unit.monthly_fixed_rate)}` : '-'}
                  </TableCell>
                  <TableCell>
                    {unit.cadastral_sheet && unit.cadastral_parcel ? (
                      <div className="text-sm">
                        <div>Fg. {unit.cadastral_sheet}, Part. {unit.cadastral_parcel}</div>
                        {unit.cadastral_subordinate && <div>Sub. {unit.cadastral_subordinate}</div>}
                        {unit.cadastral_category && <div>Cat. {unit.cadastral_category}</div>}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEstrattoDialog(unit)}
                        title="Genera Estratto Conto"
                        data-testid={`estratto-unit-${unit.id}`}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(unit)}
                        data-testid={`edit-unit-${unit.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(unit.id)}
                        className="text-red-700 hover:text-red-900 hover:bg-red-50"
                        data-testid={`delete-unit-${unit.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <DocumentPreview
        open={!!anteprimaEstratto}
        onOpenChange={(v) => {
          if (!v) {
            if (anteprimaEstratto?.url) window.URL.revokeObjectURL(anteprimaEstratto.url);
            setAnteprimaEstratto(null);
          }
        }}
        url={anteprimaEstratto?.url}
        titolo={anteprimaEstratto?.titolo}
        nomeFile={anteprimaEstratto?.nomeFile}
        descrizione="Il documento è già archiviato nei Documenti dell'unità. Salvarlo o stamparlo resta una tua scelta."
        formato="pdf"
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUnit ? 'Modifica Unità' : 'Nuova Unità'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="unit_number">Numero Unità *</Label>
                <Input
                  id="unit_number"
                  value={formData.unit_number}
                  onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                  placeholder="A1"
                  required
                  data-testid="unit-number-input"
                />
              </div>
              <div>
                <Label htmlFor="floor">Piano</Label>
                <Input
                  id="floor"
                  type="number"
                  value={formData.floor}
                  onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                  placeholder="1"
                />
              </div>
              <div>
                <Label htmlFor="building">Scala/Edificio</Label>
                <Input
                  id="building"
                  value={formData.building}
                  onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                  placeholder="Scala A"
                />
              </div>
              <div>
                <Label htmlFor="square_meters">Superficie (m²)</Label>
                <Input
                  id="square_meters"
                  type="number"
                  step="0.1"
                  value={formData.square_meters}
                  onChange={(e) => setFormData({ ...formData, square_meters: e.target.value })}
                  placeholder="85.5"
                />
              </div>
              <div>
                <Label htmlFor="destination">Destinazione d'uso</Label>
                <Select
                  value={formData.destination || undefined}
                  onValueChange={(v) => setFormData({ ...formData, destination: v })}
                >
                  <SelectTrigger id="destination" data-testid="unit-destination-select">
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    {DESTINAZIONI.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="monthly_fixed_rate">Rata mensile deliberata (€)</Label>
                <Input
                  id="monthly_fixed_rate"
                  type="number"
                  step="0.01"
                  value={formData.monthly_fixed_rate}
                  onChange={(e) =>
                    setFormData({ ...formData, monthly_fixed_rate: e.target.value })
                  }
                  placeholder="0,00"
                  data-testid="unit-monthly-rate-input"
                />
                <p className="text-xs text-stone-500 mt-1">
                  Usata dalle rate in modalità «rata fissa» e dai suggerimenti di
                  attribuzione degli incassi. Lasciare vuoto se la quota si calcola
                  solo a millesimi.
                </p>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Dati Catastali</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="cadastral_sheet">Foglio</Label>
                  <Input
                    id="cadastral_sheet"
                    value={formData.cadastral_sheet}
                    onChange={(e) => setFormData({ ...formData, cadastral_sheet: e.target.value })}
                    placeholder="123"
                  />
                </div>
                <div>
                  <Label htmlFor="cadastral_parcel">Particella</Label>
                  <Input
                    id="cadastral_parcel"
                    value={formData.cadastral_parcel}
                    onChange={(e) => setFormData({ ...formData, cadastral_parcel: e.target.value })}
                    placeholder="456"
                  />
                </div>
                <div>
                  <Label htmlFor="cadastral_subordinate">Subalterno</Label>
                  <Input
                    id="cadastral_subordinate"
                    value={formData.cadastral_subordinate}
                    onChange={(e) => setFormData({ ...formData, cadastral_subordinate: e.target.value })}
                    placeholder="1"
                  />
                </div>
                <div>
                  <Label htmlFor="cadastral_category">Categoria</Label>
                  <Input
                    id="cadastral_category"
                    value={formData.cadastral_category}
                    onChange={(e) => setFormData({ ...formData, cadastral_category: e.target.value })}
                    placeholder="A/2"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="cadastral_income">Rendita Catastale (€)</Label>
                  <Input
                    id="cadastral_income"
                    type="number"
                    step="0.01"
                    value={formData.cadastral_income}
                    onChange={(e) => setFormData({ ...formData, cadastral_income: e.target.value })}
                    placeholder="450.00"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Note</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Note aggiuntive..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={loading} 
                data-testid="submit-unit-form"
              >
                {loading ? 'Salvataggio...' : 'Salva'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Estratto Conto Dialog */}
      <Dialog open={estrattoDialogOpen} onOpenChange={setEstrattoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Genera Estratto Conto</DialogTitle>
            <DialogDescription>
              Genera l'estratto conto personale per l'unità {selectedUnitForEstratto?.unit_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="estratto-year">Anno di riferimento</Label>
              <Select value={estrattoYear} onValueChange={setEstrattoYear}>
                <SelectTrigger id="estratto-year">
                  <SelectValue placeholder="Seleziona anno" />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(5)].map((_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium">ℹ️ Informazioni</p>
              <p>Il documento generato sarà visibile solo al proprietario/inquilino dell'unità.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEstrattoDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleGenerateEstratto} disabled={generatingPdf}>
              {generatingPdf ? (
                <>Generazione...</>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Genera PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <ExcelImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        importType="units"
        condominiumId={condominiumId}
        onSuccess={() => {
          fetchUnits();
          setImportDialogOpen(false);
        }}
      />
    </Card>
  );
};
